import useRestService from '../shared/rest-service';
import Client from '../models/client';
import { useOktaAuth } from '@okta/okta-react';

/**
 * useClient
 * @function useClient - Custom hook for the client models
 */
const useClient = () => {
  const { getAuth, postAuth, postFileAuth, putAuth, baseURL } = useRestService();
  const { oktaAuth } = useOktaAuth();
  const accessToken = oktaAuth.getAccessToken();

  const fetchClients = (
    currentPage: number,
    pageSize: number,
    searchTerm?: string,
    orderColumn?: string,
    orderDirection?: string
  ) => {
    return getClients('clients', currentPage, pageSize, searchTerm, orderColumn, orderDirection);
  };

  const fetchAdminClients = (
    currentPage: number,
    pageSize: number,
    searchTerm?: string,
    orderColumn?: string,
    orderDirection?: string
  ) => {
    return getClients('clients/admin', currentPage, pageSize, searchTerm, orderColumn, orderDirection);
  };

  const getClients = (
    path: string,
    currentPage: number,
    pageSize: number,
    searchTerm?: string,
    orderColumn?: string,
    orderDirection?: string
  ) => {
    let queryParams = `?page=${currentPage}&pageSize=${pageSize}`;
    if (!(searchTerm === null || searchTerm === undefined) && searchTerm.trim() !== '') {
      queryParams += `&searchTerm=${searchTerm}`;
    }
    if (orderColumn != null) {
      queryParams += `&sort=${orderColumn}`;
    }
    if (orderDirection != null) {
      queryParams += `&sortDirection=${orderDirection}`;
    }
    return getAuth(`api/${path}${queryParams}`);
  };

  const fetchAllClients = () => {
    let queryParams = `?page=1&pageSize=0`;
    return getAuth(`api/clients${queryParams}`);
  };

  const createClient = (client: Client) => {
    return postAuth('api/clients', client);
  };

  const storeLogo = (clientId: number, file: any) => {
    return postFileAuth('api/clients/' + clientId + '/logo', null, file);
  };

  const fetchClient = (clientId: string): Promise<Client> => {
    return getAuth(`api/clients/${clientId}`);
  };

  const editClient = (client: Client): Promise<any> => {
    return putAuth(`api/clients/${client.clientId}`, client);
  };

  const fetchClientBrandImage = (brandImage: string): string => {
    return `${baseURL}api/assets?path=${brandImage}&accessToken=${accessToken}`;
  };

  return {
    fetchClients,
    fetchAdminClients,
    fetchAllClients,
    fetchClient,
    createClient,
    storeLogo,
    fetchClientBrandImage,
    editClient
  };
};

export default useClient;
